import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientSlider from "../components/home/ClientSlider";
import ServiceArea from "../components/serviceArea";
import Seo from "../components/Seo";
import { loadSection } from "../services/contentSource";
import pageFallback from "../data/servicePage.json";

const Service = () => {
  const [page, setPage] = useState<any>(pageFallback);

  useEffect(() => {
    loadSection("servicePage", pageFallback as any).then(({ data: main }) => {
      if (main) setPage(main);
    });
  }, []);

  const heroTitle = page?.hero?.title || "INTELLIGENT SERVICES ENGINEERED FOR EXPONENTIAL GROWTH";
  const heroLede =
    page?.hero?.lede ||
    "We engineer future-ready AI systems, cloud-native platforms, and intuitive digital experiences that transform complex workflows into scalable competitive advantages.";

  return (
    <>
      <Seo
        title="Enterprise AI Services | MindSyncX"
        description="Custom AI agents, full-stack architecture, computer vision engineering and cloud infrastructure, delivered by the team that runs them."
        path="/services"
      />

      <main className="mx-services-page-revamp">
        {/* 1. Hero Area */}
        <section className="mx-services-hero-section">
          <div className="container mx-container">
            <div className="mx-about-hero-header">
              <div className="mx-hero-badge">
                <span className="mx-hero-badge-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </span>
                <span>01. CAPABILITIES &amp; SYSTEMS</span>
              </div>

              <h1 className="mx-services-hero-title">
                {heroTitle}
              </h1>

              <p className="mx-services-hero-lede">
                {heroLede}
              </p>
            </div>
          </div>
        </section>

        {/* 2. Client Ecosystem Marquee */}
        <ClientSlider label="TRUSTED BY HIGH-GROWTH STARTUPS &amp; ENTERPRISE INNOVATORS" />

        {/* 3. Core Enterprise Services Grid */}
        <ServiceArea />

        {/* 4. Enterprise Delivery Blueprint Showcase */}
        <section className="mx-showcase-section">
          <div className="container mx-container">
            <div className="mx-showcase-card">
              <div className="mx-showcase-content">
                <div className="mx-showcase-badge">
                  <span className="mx-showcase-badge-dot"></span>
                  <span>{page.aboutBlock?.badge || "DELIVERY BLUEPRINT & FRAMEWORK"}</span>
                </div>

                <h2 className="mx-showcase-title">
                  {page.aboutBlock?.title || "PREDICTABLE, SPRINT-DRIVEN EXECUTION FROM IDEA TO SCALE"}
                </h2>

                <p className="mx-showcase-text">
                  {page.aboutBlock?.textBefore || "Whether engineering an AI agent ecosystem, modernizing core enterprise systems, or shipping a flagship web and mobile platform, our battle-tested lifecycle ensures rapid weekly turnaround, transparent milestones, and production-grade resilience."}
                </p>

                <div className="mx-showcase-features">
                  <div className="mx-showcase-feature-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Weekly Staging Deployments</span>
                  </div>
                  <div className="mx-showcase-feature-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Zero-Trust Security &amp; QA</span>
                  </div>
                  <div className="mx-showcase-feature-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Dedicated Senior Squads</span>
                  </div>
                </div>

                <div className="mx-showcase-btn-group">
                  <Link to={page.aboutBlock?.buttonLink || "/contact"} className="mx-showcase-btn">
                    <span>{page.aboutBlock?.buttonText || "Start An Engagement"}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </Link>

                  <Link to="/case-studies" className="mx-showcase-btn-secondary">
                    <span>View Case Studies</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="mx-showcase-visual-panel">
                <div className="mx-showcase-terminal-header">
                  <div className="mx-showcase-terminal-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="mx-showcase-terminal-status">DELIVERY LIFECYCLE // EXECUTION BENCHMARKS</div>
                </div>

                <div className="mx-dashboard-status-banner">
                  <div className="mx-dashboard-status-indicator">
                    <span className="mx-dashboard-pulse-dot"></span>
                    <span className="mx-dashboard-status-title">Agile Velocity Enabled</span>
                  </div>
                  <span className="mx-dashboard-status-sub">Predictable, high-cadence production sprints</span>
                </div>

                <div className="mx-dashboard-metrics-grid">
                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">SPRINT CADENCE</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">Weekly</div>
                    <div className="mx-dashboard-metric-note">Deployable staging builds</div>
                  </div>

                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">ON-TIME DELIVERY</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">99.4%</div>
                    <div className="mx-dashboard-metric-note">Transparent milestones &amp; scope</div>
                  </div>

                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">TECH STACK</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">Full-Stack AI</div>
                    <div className="mx-dashboard-metric-note">Modern cloud &amp; edge native</div>
                  </div>

                  <div className="mx-dashboard-metric-card">
                    <div className="mx-dashboard-metric-header">
                      <span className="mx-dashboard-metric-label">LIFECYCLE SUPPORT</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div className="mx-dashboard-metric-value">360°</div>
                    <div className="mx-dashboard-metric-note">Strategy, launch &amp; scaling</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Closing Call to Action */}
        <section className="mx-page-cta-section">
          <div className="container mx-container">
            <div className="mx-page-cta-box">
              <h2 className="mx-page-cta-title">
                {page.closingCta?.title || "START YOUR EXPERIENCE WITH MINDSYNCX"}
              </h2>
              <Link to="/contact" className="mx-page-cta-btn">
                <span>{page.closingCta?.buttonText || "Let’s get in touch"}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Service;
